import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, Loader2 } from 'lucide-react';
import { getRequest, postRequest } from "../../services/apiService"; // Assuming postRequest is available
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddNewFormModal = () => {
  const [taskLevels, setTaskLevels] = useState([[]]);
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [systems, setSystems] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [milestones, setMilestones] = useState([]);

  // New State Variables
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actualStartDate, setActualStartDate] = useState('');
  const [actualFinishDate, setActualFinishDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [actualCompletionPercent, setActualCompletionPercent] = useState(0);

  const [units, setUnits] = useState([]);

  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingShips, setLoadingShips] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const [searchTermTask, setSearchTermTask] = useState('');
  const [isTasksDropdownOpen, setIsTasksDropdownOpen] = useState(null);
  const [selectedTaskPath, setSelectedTaskPath] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null); // To store the ID of the fully selected task

  const [searchTermProject, setSearchTermProject] = useState('');
  const [isProjectsDropdownOpen, setIsProjectsDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');

  const [searchTermShip, setSearchTermShip] = useState('');
  const [isShipsDropdownOpen, setIsShipsDropdownOpen] = useState(false);
  const [selectedShip, setSelectedShip] = useState('');

  const [isEquipmentDropdownOpen, setIsEquipmentDropdownOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [searchTermEquipment, setSearchTermEquipment] = useState('');

  const [isSystemDropdownOpen, setIsSystemDropdownOpen] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [searchTermSystem, setSearchTermSystem] = useState('');

  const [isBlockDropdownOpen, setIsBlockDropdownOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState('');
  const [searchTermBlock, setSearchTermBlock] = useState('');

  const [isMilestoneDropdownOpen, setIsMilestoneDropdownOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [searchTermMilestone, setSearchTermMilestone] = useState('');

  // New state for Unit dropdown
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [searchTermUnit, setSearchTermUnit] = useState('');

  const [addTaskMode, setAddTaskMode] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // New state to manage disabling of dropdowns
  const [activeDropdownGroup, setActiveDropdownGroup] = useState(null); // 'equipment', 'system', 'block', 'unit'


  const tasksDropdownRef = useRef([]);
  const projectsDropdownRef = useRef(null);
  const shipsDropdownRef = useRef(null);
  const equipmentDropdownRef = useRef(null);
  const systemDropdownRef = useRef(null);
  const blockDropdownRef = useRef(null);
  const milestoneDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null); // Ref for Unit dropdown

  const showToast = (severity, detail) => {
    if (severity === 'error') {
      toast.error(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (severity === 'success') {
      toast.success(detail, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await getRequest('/master/projects/');
      const projectList = response.data || [];
      setProjects(projectList.map(item => ({ id: item.id, name: item.name })));
    } catch (err) {
      console.error("Error fetching projects:", err);
      showToast('error', 'Failed to fetch projects');
      setError("Failed to load projects for dropdown");
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchShips = async (projectId) => {
    if (!projectId) {
      setShips([]);
      return;
    }
    setLoadingShips(true);
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      const shipList = response.data || [];
      setShips(shipList.map(item => ({ id: item.id, name: item.name })));
    } catch (err) {
      console.error("Error fetching ships:", err);
      showToast('error', 'Failed to fetch ships for the selected project');
      setShips([]);
    } finally {
      setLoadingShips(false);
    }
  };

  const fetchTasks = async (parentId = null, shipId, levelIndex) => {
    if (!shipId) {
      setTaskLevels([[]]);
      setSelectedTaskPath([]);
      setSelectedTaskId(null);
      setLoadingTasks(false);
      return;
    }

    setLoadingTasks(true);
    try {
      const url = parentId
        ? `/pert/task-transaction-api/?parent_task_id=${parentId}&project_id=${shipId}`
        : `/pert/task-transaction-api/?parent_task_id=null&project_id=${shipId}`;
      const response = await getRequest(url);
      const tasksData = response.data || [];
      setTaskLevels(prevLevels => {
        const newLevels = [...prevLevels];
        while (newLevels.length <= levelIndex) {
          newLevels.push([]);
        }
        newLevels[levelIndex] = tasksData;
        if (tasksData.length > 0) {
          if (levelIndex === newLevels.length - 1) {
            newLevels.push([]);
          }
          else if (levelIndex < newLevels.length - 1) {
              newLevels.splice(levelIndex + 1);
              newLevels.push([]);
          }
        } else {
          newLevels.splice(levelIndex + 1);
        }
        return newLevels;
      });

    } catch (err) {
      console.error(`[Task Fetch] Error fetching tasks for level ${levelIndex}:`, err);
      showToast('error', 'Failed to fetch tasks');
      setError("Failed to load tasks for dropdown");
      setTaskLevels(prevLevels => {
        const newLevels = [...prevLevels];
        if (levelIndex < newLevels.length) {
            newLevels[levelIndex] = [];
        }
        if (levelIndex < newLevels.length - 1) {
            newLevels.splice(levelIndex + 1);
        }
        return newLevels;
      });
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchTaskDetails = async (taskId) => {
    if (!taskId) {
      // Clear previous details if no task is selected
      setBudget('');
      setStartDate('');
      setEndDate('');
      setActualStartDate('');
      setActualFinishDate('');
      setRemarks('');
      setActualCompletionPercent(0);
      setSelectedEquipment('');
      setSelectedSystem('');
      setSelectedBlock('');
      setSelectedUnit('');
      setSelectedMilestone('');
      return;
    }
    try {
      const response = await getRequest(`/pert/task-transaction-api/${taskId}/`);
      const taskDetails = response.data;
      if (taskDetails) {
        setBudget(taskDetails.budget || '');
        setStartDate(taskDetails.start_date || '');
        setEndDate(taskDetails.end_date || '');
        setActualStartDate(taskDetails.actual_start || '');
        setActualFinishDate(taskDetails.actual_finish || '');
        setRemarks(taskDetails.remarks || '');
        setActualCompletionPercent(taskDetails.actual_completion_percent || 0);

        // Pre-fill related dropdowns
        if (taskDetails.equipment) {
          setSelectedEquipment({ id: taskDetails.equipment.id, name: taskDetails.equipment.name, department: taskDetails.equipment.department?.name });
          setActiveDropdownGroup('equipment');
        } else if (taskDetails.system) {
          setSelectedSystem({ id: taskDetails.system.id, name: taskDetails.system.name });
          setActiveDropdownGroup('system');
        } else if (taskDetails.block) {
          setSelectedBlock({ id: taskDetails.block.id, name: taskDetails.block.name });
          setActiveDropdownGroup('block');
        } else if (taskDetails.unit) {
          setSelectedUnit({ id: taskDetails.unit.id, name: taskDetails.unit.name });
          setActiveDropdownGroup('unit');
        } else {
          setActiveDropdownGroup(null);
        }

        if (taskDetails.milestone) {
          setSelectedMilestone({ id: taskDetails.milestone.id, name: taskDetails.milestone.title });
        }
      }
    } catch (err) {
      console.error("Error fetching task details:", err);
      showToast('error', 'Failed to fetch task details');
    }
  };


  const fetchEquipment = async () => {
    setLoadingEquipment(true);
    try {
      const response = await getRequest('/master/equipment');
      const equipmentList = response.data || [];
      setEquipmentList(equipmentList.map(item => ({
        id: item.id,
        name: item.name,
        department: item.department ? item.department.name : null // Store department name
      })));
    } catch (err) {
      console.error("Error fetching equipment:", err);
      showToast('error', 'Failed to fetch equipment');
    } finally {
      setLoadingEquipment(false);
    }
  };

  const fetchSystems = async () => {
    setLoadingSystems(true);
    try {
      const response = await getRequest('/master/systems');
      const systemList = response.data || [];
      setSystems(systemList.map(item => ({ id: item.id, name: item.name })));
    } catch (err) {
      console.error("Error fetching systems:", err);
      showToast('error', 'Failed to fetch systems');
    } finally {
      setLoadingSystems(false);
    }
  };

  const fetchMilestones = async () => {
    setLoadingMilestones(true);
    try {
      const response = await getRequest('/master/milestones');
      const milestoneList = response.data || [];
      setMilestones(milestoneList.map(item => ({
        id: item.id,
        name: item.title,
        dependencies: item.dependent_on || []
      })));
    } catch (err) {
      console.error("Error fetching milestones:", err);
      showToast('error', 'Failed to fetch milestones');
    } finally {
      setLoadingMilestones(false);
    }
  };

  const fetchUnits = async (projectId) => {
  if (!projectId) {
    setUnits([]);
    return;
  }

  setLoadingUnits(true);
  try {
    const response = await getRequest(`/transaction/unit-transaction/?project_id=${projectId}`);

    // Get the actual data array (handle both direct array and { data: array } responses)
    const responseData = Array.isArray(response) ? response :
                        Array.isArray(response.data) ? response.data :
                        [];

    // Transform the data - use transaction name (item.name) as display value
    const units = responseData.map(item => ({
      id: item.id, // transaction ID
      name: item.name || 'Unnamed Unit', // "TTPM1", "TTPM2" etc.
      unitId: item.unit?.id, // optional: preserve unit ID if needed
      unitName: item.unit?.name // optional: preserve unit technical name
    }));

    setUnits(units);

  } catch (err) {
    console.error("fetchUnits: Error:", {
      error: err,
      response: err.response?.data
    });
    showToast('error', 'Failed to load units');
    setUnits([]);
  } finally {
    setLoadingUnits(false);
  }
};

  // New function to fetch blocks
  const fetchBlocks = async (projectId) => {
  if (!projectId) {
    setBlocks([]);
    return;
  }

  setLoadingBlocks(true);
  try {
    const url = `/transaction/block-transaction/?project_id=${projectId}`;
    const response = await getRequest(url);
    // Handle both direct array and { data: array } responses
    const responseData = Array.isArray(response) ? response :
                        Array.isArray(response.data) ? response.data :
                        [];

    const blocks = responseData.map(item => {
      return {
        id: item.block?.id,
        name: item.block?.name || 'Unnamed Block'
      };
    });

    setBlocks(blocks);

  } catch (err) {
    console.error("Error fetching blocks:", {
      error: err,
      response: err.response?.data
    });
    showToast('error', 'Failed to fetch blocks for the selected project');
    setBlocks([]);
  } finally {
    setLoadingBlocks(false);
  }
};

  useEffect(() => {
    fetchProjects();
    fetchEquipment();
    fetchSystems();
    fetchMilestones();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchShips(selectedProject.id);
      fetchUnits(selectedProject.id); // Fetch units when a project is selected
      fetchBlocks(selectedProject.id); // Fetch blocks when a project is selected
    } else {
      setShips([]);
      setSelectedShip('');
      setTaskLevels([[]]);
      setSelectedTaskPath([]);
      setSelectedTaskId(null); // Clear selected task ID
      setIsTasksDropdownOpen(null);
      setAddTaskMode(false);
      setNewTaskName('');
      setSearchTermTask('');
      setUnits([]); // Clear units when no project is selected
      setSelectedUnit(''); // Clear selected unit
      setBlocks([]); // Clear blocks when no project is selected
      setSelectedBlock(''); // Clear selected block
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedShip) {
      fetchTasks(null, selectedShip.id, 0);
    } else {
      setTaskLevels([[]]);
      setSelectedTaskPath([]);
      setSelectedTaskId(null); // Clear selected task ID
      setIsTasksDropdownOpen(null);
      setAddTaskMode(false);
      setNewTaskName('');
      setSearchTermTask('');
    }
  }, [selectedShip]);

  useEffect(() => {
    // Fetch details when a specific task is selected (i.e., when selectedTaskPath changes and the last item is a complete task)
    if (selectedTaskPath.length > 0) {
      const lastSelectedTask = selectedTaskPath[selectedTaskPath.length - 1];
      setSelectedTaskId(lastSelectedTask.id);
    } else {
      setSelectedTaskId(null);
    }
  }, [selectedTaskPath]);

  useEffect(() => {
    fetchTaskDetails(selectedTaskId);
  }, [selectedTaskId]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      tasksDropdownRef.current.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          if (isTasksDropdownOpen === index) {
            setIsTasksDropdownOpen(null);
            if (addTaskMode) {
              // setAddTaskMode(false); // Do not exit add task mode here
              // setNewTaskName('');
              // setErrorMessage('');
            }
          }
        }
      });

      if (projectsDropdownRef.current && !projectsDropdownRef.current.contains(event.target)) {
        setIsProjectsDropdownOpen(false);
      }
      if (shipsDropdownRef.current && !shipsDropdownRef.current.contains(event.target)) {
        setIsShipsDropdownOpen(false);
      }
      if (equipmentDropdownRef.current && !equipmentDropdownRef.current.contains(event.target)) {
        setIsEquipmentDropdownOpen(false);
      }
      if (systemDropdownRef.current && !systemDropdownRef.current.contains(event.target)) {
        setIsSystemDropdownOpen(false);
      }
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(event.target)) {
        setIsBlockDropdownOpen(false);
      }
      if (milestoneDropdownRef.current && !milestoneDropdownRef.current.contains(event.target)) {
        setIsMilestoneDropdownOpen(false);
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) { // New ref for Unit
        setIsUnitDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [addTaskMode, isTasksDropdownOpen]);

  const filteredTasks = (currentLevelTasks) => {
    if (!currentLevelTasks) return [];
    return currentLevelTasks.filter(task =>
      task.name.toLowerCase().includes(searchTermTask.toLowerCase())
    );
  };

  const searchTermTaskNotFound = (currentLevelTasks) => {
      return searchTermTask.trim() !== '' &&
      filteredTasks(currentLevelTasks).length === 0 &&
      !currentLevelTasks.some(task => task.name.toLowerCase() === searchTermTask.toLowerCase());
  }

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTermProject.toLowerCase())
  );

  const filteredShips = ships.filter(ship =>
    ship.name.toLowerCase().includes(searchTermShip.toLowerCase())
  );

  const filteredEquipment = equipmentList.filter(item =>
    item.name.toLowerCase().includes(searchTermEquipment.toLowerCase())
  );

  const filteredSystems = systems.filter(item =>
    item.name.toLowerCase().includes(searchTermSystem.toLowerCase())
  );

  const filteredBlocks = blocks.filter(item =>
    item.name.toLowerCase().includes(searchTermBlock.toLowerCase())
  );

  const filteredMilestones = milestones.filter(item =>
    item.name.toLowerCase().includes(searchTermMilestone.toLowerCase())
  );

  const filteredUnits = units.filter(item =>
    item.name.toLowerCase().includes(searchTermUnit.toLowerCase())
  );

  const handleTaskSelect = (task, levelIndex) => {
    const newSelectedTaskPath = selectedTaskPath.slice(0, levelIndex);
    newSelectedTaskPath.push(task);
    setSelectedTaskPath(newSelectedTaskPath);
    setIsTasksDropdownOpen(levelIndex + 1);
    fetchTasks(task.id, selectedShip.id, levelIndex + 1);
    setAddTaskMode(false); // Exit add task mode when a task is selected
    setNewTaskName(''); // Clear new task name
    setSearchTermTask(''); // Clear search term
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setIsProjectsDropdownOpen(false);
    setSelectedShip('');
    setTaskLevels([[]]);
    setSelectedTaskPath([]);
    setSelectedTaskId(null);
    setIsTasksDropdownOpen(null);
    setAddTaskMode(false);
    setNewTaskName('');
    setSearchTermTask('');
    // fetchUnits and fetchBlocks are now in useEffect for selectedProject
  };

  const handleShipSelect = (ship) => {
    setSelectedShip(ship);
    setIsShipsDropdownOpen(false);
    setTaskLevels([[]]);
    setSelectedTaskPath([]);
    setSelectedTaskId(null);
    setIsTasksDropdownOpen(null);
    setAddTaskMode(false);
    setNewTaskName('');
    setSearchTermTask('');
  };

  const handleEquipmentSelect = (equipment) => {
    setSelectedEquipment(equipment);
    setIsEquipmentDropdownOpen(false);
    setActiveDropdownGroup('equipment'); // Set active group
    setSelectedSystem(''); // Clear other selections
    setSelectedBlock('');
    setSelectedUnit('');
  };

  const handleSystemSelect = (system) => {
    setSelectedSystem(system);
    setIsSystemDropdownOpen(false);
    setActiveDropdownGroup('system'); // Set active group
    setSelectedEquipment(''); // Clear other selections
    setSelectedBlock('');
    setSelectedUnit('');
  };

  const handleBlockSelect = (block) => {
    setSelectedBlock(block);
    setIsBlockDropdownOpen(false);
    setActiveDropdownGroup('block'); // Set active group
    setSelectedEquipment(''); // Clear other selections
    setSelectedSystem('');
    setSelectedUnit('');
  };

  const handleUnitSelect = (unit) => { // New handler for Unit
    setSelectedUnit(unit);
    setIsUnitDropdownOpen(false);
    setActiveDropdownGroup('unit'); // Set active group
    setSelectedEquipment(''); // Clear other selections
    setSelectedSystem('');
    setSelectedBlock('');
  };

  const handleMilestoneSelect = (milestone) => {
    setSelectedMilestone(milestone);
    setIsMilestoneDropdownOpen(false);
  };

  const toggleAddTaskMode = (levelIndex) => {
    setAddTaskMode(true);
    setNewTaskName(searchTermTask);
    setErrorMessage('');
    // Ensure the current dropdown stays open for input
    setIsTasksDropdownOpen(levelIndex);
  };

  const handleCancelAddTask = () => {
    setAddTaskMode(false);
    setNewTaskName('');
    setErrorMessage('');
    // setSearchTermTask(''); // Do not clear search term here, as it might be used to pre-fill new task name
  };


  const handleSubmit = async () => {
    if (!selectedProject || !selectedShip || (!selectedTaskId && !addTaskMode)) {
      showToast('error', 'Please select a Project, Ship, and a Task (or add a new one).');
      return;
    }

    if (addTaskMode && !newTaskName.trim()) {
      setErrorMessage('Task Name cannot be empty when adding a new task.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    let taskType = null;
    if (selectedSystem) {
      taskType = 'System';
    } else if (selectedBlock) {
      taskType = 'Block';
    } else if (selectedUnit) {
      taskType = 'Unit';
    } else if (selectedEquipment && selectedEquipment.department) {
      taskType = selectedEquipment.department; // Use department name as task type
    } else if (selectedMilestone) {
      taskType = 'Milestone';
    }


    const payload = {
      project: selectedShip.id,
      // ship_id: selectedShip.id,
      name: addTaskMode ? newTaskName.trim() : (selectedTaskPath.length > 0 ? selectedTaskPath[selectedTaskPath.length - 1].name : ''),
      parent_task: selectedTaskPath.length > 1 ? selectedTaskPath[selectedTaskPath.length - 2].id : null,
      equipment: selectedEquipment ? selectedEquipment.id : null,
      system: selectedSystem ? selectedSystem.id : null,
      block: selectedBlock ? selectedBlock.id : null,
      unit: selectedUnit ? selectedUnit.id : null,
      milestone: selectedMilestone ? selectedMilestone.id : null,
      budget: budget || null,
      start_date: startDate || null,
      end_date: endDate || null,
      actual_start: actualStartDate || null,
      actual_finish: actualFinishDate || null,
      actual_completion_percent: actualCompletionPercent,
      remarks: remarks || null,
      task_type: taskType, // Added task_type
    };

    try {
      let response;
      if (selectedTaskId && !addTaskMode) {
        // If an existing task is selected, we might want to update it.
        // The API response indicates a POST for new tasks, so assuming update would be PUT/PATCH on specific ID
        // For this scenario, we will treat it as adding a new task if addTaskMode is active, otherwise
        // if an existing task is selected, we'll just log its details, not update with this form.
        // To handle update, you would need a separate flow or a check if selectedTaskId points to an editable task.
        // For now, if selectedTaskId is present and addTaskMode is OFF, we consider this a display-only of existing task.
        // If addTaskMode is ON, it's always a new task.
        showToast('error', 'Update functionality not implemented for existing tasks via this form. Please create a new task or modify an existing one directly.');
        setIsSubmitting(false);
        return;

      } else {
        // This is for adding a NEW task (either top-level or sub-task)
        response = await postRequest('/pert/task-transaction-api/', payload);
      }

      if (response.status === 'success' || response.status === 201 || response.status === 200) {
        showToast('success', 'Task submitted successfully!');
        // Clear form after successful submission
        setBudget('');
        setStartDate('');
        setEndDate('');
        setActualStartDate('');
        setActualFinishDate('');
        setRemarks('');
        setActualCompletionPercent(0);
        setSelectedEquipment('');
        setSelectedSystem('');
        setSelectedBlock('');
        setSelectedUnit('');
        setSelectedMilestone('');
        setAddTaskMode(false);
        setNewTaskName('');
        setSearchTermTask('');
        setSelectedTaskPath([]);
        setSelectedTaskId(null);
        fetchTasks(selectedTaskPath.length > 1 ? selectedTaskPath[selectedTaskPath.length - 2].id : null, selectedShip.id, selectedTaskPath.length > 0 ? selectedTaskPath.length -1 : 0 ); // Refresh tasks
        setActiveDropdownGroup(null); // Reset active group
      } else {
        showToast('error', response.message || 'Failed to submit task.');
      }
    } catch (err) {
      console.error("Submission Error:", err);
      showToast('error', err.response?.data?.message || 'An error occurred during submission.');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (loadingProjects) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Loading data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <ToastContainer />

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative" ref={projectsDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Select Project</div>
            <div
              className="border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm"
              onClick={() => setIsProjectsDropdownOpen(!isProjectsDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedProject ? selectedProject.name : "Select a project..."}
              </div>
              <div className="relative">
                {loadingProjects ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsProjectsDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermProject(e.target.value);
                  }}
                  value={searchTermProject}
                />
              </div>
            </div>

            {isProjectsDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search projects..."
                    value={searchTermProject}
                    onChange={(e) => setSearchTermProject(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingProjects ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading projects...</p>
                    </div>
                  ) : (
                    <>
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((project) => (
                          <div
                            key={project.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleProjectSelect(project)}
                          >
                            {project.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No projects found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={shipsDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Select Ship</div>
            <div
              className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${!selectedProject ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => selectedProject && setIsShipsDropdownOpen(!isShipsDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedShip ? selectedShip.name : "Select a ship..."}
              </div>
              <div className="relative">
                {loadingShips ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectedProject && setIsShipsDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermShip(e.target.value);
                  }}
                  value={searchTermShip}
                  disabled={!selectedProject}
                />
              </div>
            </div>

            {isShipsDropdownOpen && selectedProject && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search ships..."
                    value={searchTermShip}
                    onChange={(e) => setSearchTermShip(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingShips ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading ships...</p>
                    </div>
                  ) : (
                    <>
                      {filteredShips.length > 0 ? (
                        filteredShips.map((ship) => (
                          <div
                            key={ship.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleShipSelect(ship)}
                          >
                            {ship.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No ships found for this project</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {taskLevels.map((tasksAtLevel, levelIndex) => {
            if (!selectedShip || levelIndex > selectedTaskPath.length + (addTaskMode ? 1 : 0)) { // Show current and next level if adding task
                return null;
            }

            const currentSelectedTask = selectedTaskPath[levelIndex];
            const displayValue = addTaskMode && isTasksDropdownOpen === levelIndex ? newTaskName :
                                 (currentSelectedTask ? currentSelectedTask.name :
                                 (levelIndex === 0 ? "Select a task..." : "Select sub-task..."));

            const isInteractive = levelIndex === 0 || selectedTaskPath[levelIndex - 1];

            const showAddOption = (isInteractive && searchTermTask.trim() !== '' && filteredTasks(tasksAtLevel).length === 0) ||
                                  (isInteractive && tasksAtLevel.length === 0 && searchTermTask.trim() === '');


            return (
              <div
                className="relative"
                ref={el => tasksDropdownRef.current[levelIndex] = el}
                key={levelIndex}
              >
                <div className="text-gray-700 mb-2 font-medium">
                  {levelIndex === 0 ? "Select Task" : (levelIndex === 1 ? "Select Sub-Task" : `Select Sub-Task ${levelIndex + 1}`)}
                </div>
                <div
                  className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${!isInteractive || (levelIndex > 0 && !selectedTaskPath[levelIndex - 1]) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => isInteractive && setIsTasksDropdownOpen(levelIndex)}
                >
                  <div className="flex-1 truncate">
                    {displayValue}
                  </div>
                  <div className="relative">
                    {loadingTasks && isTasksDropdownOpen === levelIndex ? (
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    ) : (
                      <Search className="h-5 w-5 text-gray-400" />
                    )}
                    <input
                      type="text"
                      className="absolute inset-0 w-full opacity-0 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        isInteractive && setIsTasksDropdownOpen(levelIndex);
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSearchTermTask(e.target.value);
                        if (addTaskMode && isTasksDropdownOpen === levelIndex) {
                          setNewTaskName(e.target.value);
                        } else {
                           // If not in add task mode, and typing, clear selected task path for this level and below
                           const newPath = selectedTaskPath.slice(0, levelIndex);
                           setSelectedTaskPath(newPath);
                           setSelectedTaskId(null); // Clear selected task ID as a new search is happening
                           setAddTaskMode(false); // Exit add task mode if user starts typing in a normal dropdown
                           setNewTaskName('');
                        }
                      }}
                      value={isTasksDropdownOpen === levelIndex && addTaskMode ? newTaskName : searchTermTask}
                      disabled={!isInteractive}
                    />
                  </div>
                </div>

                {isTasksDropdownOpen === levelIndex && isInteractive && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Search tasks..."
                        value={searchTermTask}
                        onChange={(e) => {
                          setSearchTermTask(e.target.value);
                          // If user types and add task mode is active, update new task name
                          if (addTaskMode) {
                            setNewTaskName(e.target.value);
                          } else {
                            // If user types in search while not in addTaskMode, clear deeper levels of selected task path
                            const newPath = selectedTaskPath.slice(0, levelIndex);
                            setSelectedTaskPath(newPath);
                            setSelectedTaskId(null); // Clear selected task ID
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div>
                      {loadingTasks && isTasksDropdownOpen === levelIndex ? (
                        <div className="p-4 text-center">
                          <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                          <p className="mt-2 text-gray-500">Loading tasks...</p>
                        </div>
                      ) : (
                        <>
                          {filteredTasks(tasksAtLevel).length > 0 ? (
                            filteredTasks(tasksAtLevel).map((task) => (
                              <div
                                key={task.id}
                                className="p-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleTaskSelect(task, levelIndex)}
                              >
                                {task.name}
                              </div>
                            ))
                          ) : (
                            <div className="p-2 text-gray-500 text-center">No tasks found</div>
                          )}

                          {showAddOption && (
                            <div
                              className="p-2 border-t flex items-center justify-center bg-blue-50 hover:bg-blue-100 cursor-pointer text-blue-600"
                              onClick={() => toggleAddTaskMode(levelIndex)}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              <span>Add {searchTermTask ? `"${searchTermTask}" as new` : 'New'} Task</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* This block is now primarily for showing the input field for new task name
                    when in addTaskMode. The actual submission is via the main submit button. */}
                {isTasksDropdownOpen === levelIndex && addTaskMode && isInteractive && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg overflow-visible p-3">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium text-gray-800">Add New Task</h3>
                      <button
                        className="text-gray-500 hover:text-gray-700"
                        onClick={handleCancelAddTask}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {errorMessage && (
                      <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                        {errorMessage}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Task Name
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newTaskName}
                        onChange={(e) => setNewTaskName(e.target.value)}
                        placeholder="Enter task name"
                      />
                    </div>
                     <div className="p-2 text-gray-500 text-center text-sm">
                        Fill other details below and click "Submit" to add this new task.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative" ref={equipmentDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Equipment</div>
            <div
              className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${activeDropdownGroup && activeDropdownGroup !== 'equipment' ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !(activeDropdownGroup && activeDropdownGroup !== 'equipment') && setIsEquipmentDropdownOpen(!isEquipmentDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedEquipment ? selectedEquipment.name : "Select equipment..."}
              </div>
              <div className="relative">
                {loadingEquipment ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    !(activeDropdownGroup && activeDropdownGroup !== 'equipment') && setIsEquipmentDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermEquipment(e.target.value);
                  }}
                  value={searchTermEquipment}
                  disabled={activeDropdownGroup && activeDropdownGroup !== 'equipment'}
                />
              </div>
            </div>

            {isEquipmentDropdownOpen && !(activeDropdownGroup && activeDropdownGroup !== 'equipment') && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search equipment..."
                    value={searchTermEquipment}
                    onChange={(e) => setSearchTermEquipment(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingEquipment ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading equipment...</p>
                    </div>
                  ) : (
                    <>
                      {filteredEquipment.length > 0 ? (
                        filteredEquipment.map((equipment) => (
                          <div
                            key={equipment.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleEquipmentSelect(equipment)}
                          >
                            {equipment.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No equipment found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={systemDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">System</div>
            <div
              className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${activeDropdownGroup && activeDropdownGroup !== 'system' ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !(activeDropdownGroup && activeDropdownGroup !== 'system') && setIsSystemDropdownOpen(!isSystemDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedSystem ? selectedSystem.name : "Select system..."}
              </div>
              <div className="relative">
                {loadingSystems ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    !(activeDropdownGroup && activeDropdownGroup !== 'system') && setIsSystemDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermSystem(e.target.value);
                  }}
                  value={searchTermSystem}
                  disabled={activeDropdownGroup && activeDropdownGroup !== 'system'}
                />
              </div>
            </div>

            {isSystemDropdownOpen && !(activeDropdownGroup && activeDropdownGroup !== 'system') && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search systems..."
                    value={searchTermSystem}
                    onChange={(e) => setSearchTermSystem(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingSystems ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading systems...</p>
                    </div>
                  ) : (
                    <>
                      {filteredSystems.length > 0 ? (
                        filteredSystems.map((system) => (
                          <div
                            key={system.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleSystemSelect(system)}
                          >
                            {system.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No systems found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={blockDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Block</div>
            <div
              className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'block') ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'block')) && setIsBlockDropdownOpen(!isBlockDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedBlock ? selectedBlock.name : "Select block..."}
              </div>
              <div className="relative">
                {loadingBlocks ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'block')) && setIsBlockDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermBlock(e.target.value);
                  }}
                  value={searchTermBlock}
                  disabled={!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'block')}
                />
              </div>
            </div>

            {isBlockDropdownOpen && !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'block')) && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search blocks..."
                    value={searchTermBlock}
                    onChange={(e) => setSearchTermBlock(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingBlocks ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading blocks...</p>
                    </div>
                  ) : (
                    <>
                      {filteredBlocks.length > 0 ? (
                        filteredBlocks.map((block) => (
                          <div
                            key={block.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleBlockSelect(block)}
                          >
                            {block.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No blocks found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* New Unit Dropdown */}
          <div className="relative" ref={unitDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Unit</div>
            <div
              className={`border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm ${!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'unit') ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'unit')) && setIsUnitDropdownOpen(!isUnitDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedUnit ? selectedUnit.name : "Select unit..."}
              </div>
              <div className="relative">
                {loadingUnits ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'unit')) && setIsUnitDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermUnit(e.target.value);
                  }}
                  value={searchTermUnit}
                  disabled={!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'unit')}
                />
              </div>
            </div>

            {isUnitDropdownOpen && !(!selectedProject || (activeDropdownGroup && activeDropdownGroup !== 'unit')) && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search units..."
                    value={searchTermUnit}
                    onChange={(e) => setSearchTermUnit(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingUnits ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading units...</p>
                    </div>
                  ) : (
                    <>
                      {filteredUnits.length > 0 ? (
                        filteredUnits.map((unit) => (
                          <div
                            key={unit.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleUnitSelect(unit)}
                          >
                            {unit.name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No units found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={milestoneDropdownRef}>
            <div className="text-gray-700 mb-2 font-medium">Milestone</div>
            <div
              className="border rounded-lg bg-white p-2 flex justify-between items-center cursor-pointer shadow-sm"
              onClick={() => setIsMilestoneDropdownOpen(!isMilestoneDropdownOpen)}
            >
              <div className="flex-1 truncate">
                {selectedMilestone ? selectedMilestone.name : "Select milestone..."}
              </div>
              <div className="relative">
                {loadingMilestones ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
                <input
                  type="text"
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMilestoneDropdownOpen(true);
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchTermMilestone(e.target.value);
                  }}
                  value={searchTermMilestone}
                />
              </div>
            </div>

            {isMilestoneDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search milestones..."
                    value={searchTermMilestone}
                    onChange={(e) => setSearchTermMilestone(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {loadingMilestones ? (
                    <div className="p-4 text-center">
                      <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto" />
                      <p className="mt-2 text-gray-500">Loading milestones...</p>
                    </div>
                  ) : (
                    <>
                      {filteredMilestones.length > 0 ? (
                        filteredMilestones.map((milestone) => (
                          <div
                            key={milestone.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleMilestoneSelect(milestone)}
                          >
                            <div className="font-medium">{milestone.name}</div>
                            {milestone.dependencies.length > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                Depends on: {milestone.dependencies.map(d => d.title).join(', ')}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-center">No milestones found</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New additions: Budget, Date Pickers, Remarks, Completion Slider */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="budget">
              Budget
            </label>
            <input
              type="number"
              id="budget"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="Enter budget"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="startDate">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="endDate">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="actualStartDate">
              Actual Start
            </label>
            <input
              type="date"
              id="actualStartDate"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={actualStartDate}
              onChange={(e) => setActualStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="actualFinishDate">
              Actual Finish
            </label>
            <input
              type="date"
              id="actualFinishDate"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              value={actualFinishDate}
              onChange={(e) => setActualFinishDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Actual Completion Percent: {actualCompletionPercent}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={actualCompletionPercent}
              onChange={(e) => setActualCompletionPercent(e.target.value)}
              className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer range-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-gray-700 mb-2 font-medium" htmlFor="remarks">
              Remarks
            </label>
            <textarea
              id="remarks"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              rows="3"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any remarks here..."
            ></textarea>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: isSubmitting ? 'rgb(100, 116, 139)' : 'rgb(30, 58, 138)' }}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewFormModal;